import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firebaseConfigured, getFirebase } from '@/firebase';

// Subscribes to customers/{customerId}/bible/main and returns
// bible.identity.org_name. Falls back to the provided fallback string (or
// "New Customer") when:
//   - customerId is missing
//   - firebase unconfigured
//   - doc doesn't exist yet (Bible not written)
//   - identity.org_name is unset
//   - permission denied
//
// Read-only.
export function useCustomerName(
  customerId: string | null | undefined,
  fallback?: string,
): string {
  const [name, setName] = useState<string>(fallback ?? 'New Customer');

  useEffect(() => {
    setName(fallback ?? 'New Customer');
    if (!customerId) return;
    if (!firebaseConfigured) return;
    const fb = getFirebase();
    if (!fb) return;
    const ref = doc(fb.db, 'customers', customerId, 'bible', 'main');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as Record<string, unknown> | undefined;
        const identity = data?.identity as Record<string, unknown> | undefined;
        const orgName =
          (identity?.org_name as string | undefined) ??
          (identity?.orgName as string | undefined);
        if (orgName) setName(orgName);
      },
      (_err) => {
        // Permission-denied or offline: keep fallback.
      },
    );
    return () => unsub();
  }, [customerId, fallback]);

  return name;
}
